import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, type AuthUser, type Enrollment, type Course, type CourseEdition } from "@/lib/api";
import StudentModal from "./StudentModal";
import EditStudentProfileModal from "@/components/feature/EditStudentProfileModal";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function EstudiantesPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [filterCompany, setFilterCompany] = useState("");
  const [students, setStudents] = useState<AuthUser[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editStudent, setEditStudent] = useState<AuthUser | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, enrollmentsData, coursesData, editionsData] = await Promise.all([
        api.getUsers({ includeDeleted: showDeleted }),
        api.getEnrollments(),
        api.getCourses(),
        api.getEditions(),
      ]);
      setStudents(usersData.filter((u) => u.role === "estudiante"));
      setEnrollments(enrollmentsData);
      setCourses(Array.from(new Map([...coursesData, ...editionsData.map((ed) => ed.course).filter(Boolean) as Course[]].map((course) => [course.id, course])).values()));
      setEditions(editionsData);
    } catch {
      setStudents([]);
      setEnrollments([]);
      setCourses([]);
      setEditions([]);
    }
    setLoading(false);
  }, [showDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const coursesById = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  const editionsById = useMemo(() => new Map(editions.map((e) => [e.id, e])), [editions]);

  const enrollmentMatchesFilters = useCallback(
    (en: Enrollment) => {
      const serviceId = en.course_id || en.diplomado_id;
      const editionId = en.edition_id || en.diplomado_edition_id;
      if (filters.courseId && serviceId !== filters.courseId) return false;
      if (filters.editionId && editionId !== filters.editionId) return false;
      if (filters.category) {
        const cat =
          en.edition?.course?.category ??
          en.diplomadoEdition?.diplomado?.category ??
          editionsById.get(editionId || "")?.course?.category ??
          coursesById.get(serviceId || "")?.category;
        if (cat !== filters.category) return false;
      }
      if (filters.month) {
        const startDate =
          en.edition?.start_date ?? en.diplomadoEdition?.start_date ?? editionsById.get(editionId || "")?.start_date;
        if (!matchesMonth(startDate, filters.month)) return false;
      }
      return true;
    },
    [filters, coursesById, editionsById]
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const needsEnrollmentFilter = Boolean(
      filters.courseId || filters.editionId || filters.category || filters.month
    );

    return students.filter((e) => {
      const matchSearch =
        !q ||
        e.full_name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.company.toLowerCase().includes(q);
      const matchCompany = !filterCompany || e.company === filterCompany;
      if (!matchSearch || !matchCompany) return false;

      if (needsEnrollmentFilter) {
        const hasMatch = enrollments.some(
          (en) => en.student_id === e.id && enrollmentMatchesFilters(en)
        );
        if (!hasMatch) return false;
      }
      return true;
    });
  }, [students, filters, filterCompany, enrollments, enrollmentMatchesFilters]);

  const companies = useMemo(() => {
    const set = new Set(students.map((e) => e.company).filter(Boolean));
    return Array.from(set).sort();
  }, [students]);

  const getStudentEnrollments = (studentId: string) =>
    enrollments.filter((e) => e.student_id === studentId);

  const getActiveEnrollments = (studentId: string) =>
    enrollments.filter((e) => e.student_id === studentId && e.enrollment_status !== "rechazada");

  const getStudentCertificates = (studentId: string) =>
    getStudentEnrollments(studentId).filter((e) => e.certificate_issued).length;

  const getPaymentStatusLabel = (studentId: string) => {
    const studentEnrs = getStudentEnrollments(studentId);
    const paid = studentEnrs.filter((e) => e.payment_status === "pagado" || e.payment_status === "cortesia").length;
    const total = studentEnrs.length;
    if (total === 0) return { label: "Sin cursos", class: "badge-info" };
    if (paid === total) return { label: "Al corriente", class: "badge-success" };
    if (paid > 0) return { label: "Parcial", class: "badge-warning" };
    return { label: "Pendiente", class: "badge-danger" };
  };

  const handleDelete = async (e: React.MouseEvent, student: AuthUser) => {
    e.stopPropagation();
    if (!confirm(`¿Seguro que deseas enviar a papelera a ${student.full_name}?\n\nSolo se permite si no tiene cursos inscritos ni saldo a favor.`)) return;
    try {
      await api.deleteUser(student.id);
      loadData();
    } catch (err: any) {
      alert(err.message || "Error al eliminar estudiante");
    }
  };

  const handleRestore = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("¿Seguro que deseas recuperar este estudiante?")) return;
    await api.restoreUser(id);
    loadData();
  };

  const handleExportExcel = () => {
    const data = filtered.map((student) => {
      const payment = getPaymentStatusLabel(student.id);
      return {
        "Nombre Completo": student.full_name,
        "Correo": student.email,
        "Teléfono": student.phone,
        "Empresa": student.company,
        "Cargo": student.position,
        "Cursos Inscritos": getActiveEnrollments(student.id).length,
        "Certificados Emitidos": getStudentCertificates(student.id),
        "Estado Pagos": payment.label,
        "Fecha Registro": student.created_at ? formatDate(student.created_at) : "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Estudiantes");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    saveAs(blob, `estudiantes_ksm_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Estudiantes</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Directorio de estudiantes registrados en KSM Training
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="badge-accent hidden sm:inline-flex">{filtered.length} estudiantes</span>
          <button onClick={handleExportExcel} className="btn-secondary flex items-center gap-2">
            <i className="ri-file-excel-2-line"></i> Exportar
          </button>
          <button onClick={() => setIsModalOpen(true)} className="btn-primary flex items-center gap-2">
            <i className="ri-user-add-line"></i> Nuevo Estudiante
          </button>
        </div>
      </div>

      {!loading && (
        <ListingFilters
          title="Filtros de estudiantes"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          searchPlaceholder="Buscar por nombre, correo o empresa..."
          resultCount={filtered.length}
          totalCount={students.length}
          hint="Mes filtra por fecha de inicio de la edición inscrita."
          extraActive={Boolean(filterCompany || showDeleted)}
          onClearExtras={() => {
            setFilterCompany("");
            setShowDeleted(false);
          }}
          extras={
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
                  Empresa
                </label>
                <select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  className="input-field"
                  aria-label="Filtrar por empresa"
                >
                  <option value="">Todas las empresas</option>
                  {companies.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-text-primary)] transition-colors pb-2.5">
                  <input
                    type="checkbox"
                    className="rounded border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  Ver eliminados
                </label>
              </div>
            </>
          }
        />
      )}

      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
            <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando estudiantes...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="table-header">Estudiante</th>
                    <th className="table-header">Empresa</th>
                    <th className="table-header">Cargo</th>
                    <th className="table-header">Cursos</th>
                    <th className="table-header">Certificados</th>
                    <th className="table-header">Pagos</th>
                    <th className="table-header w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((student) => {
                    const courseCount = getActiveEnrollments(student.id).length;
                    const certCount = getStudentCertificates(student.id);
                    const payment = getPaymentStatusLabel(student.id);
                    return (
                      <tr
                        key={student.id}
                        className={`hover:bg-[var(--color-bg-card-hover)] transition-colors cursor-pointer ${student.deleted_at ? "opacity-60 bg-red-500/5" : ""}`}
                        onClick={() => navigate(`/estudiantes/${student.id}`)}
                      >
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[var(--color-accent)]/15 flex items-center justify-center flex-shrink-0">
                              <span className="text-[var(--color-text-accent)] text-xs font-semibold">
                                {student.full_name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-[var(--color-text-primary)]">{student.full_name}</p>
                                {student.deleted_at && (
                                  <span className="badge-danger bg-red-500/20 text-red-500 text-[10px]">Eliminado</span>
                                )}
                              </div>
                              <p className="text-xs text-[var(--color-text-muted)]">{student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-[var(--color-text-secondary)]">{student.company}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-[var(--color-text-secondary)]">{student.position}</span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-[var(--color-text-primary)] font-medium">
                            {courseCount}
                          </span>
                        </td>
                        <td className="table-cell">
                          {certCount > 0 ? (
                            <span className="badge-success">{certCount}</span>
                          ) : (
                            <span className="text-sm text-[var(--color-text-muted)]">—</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={payment.class}>{payment.label}</span>
                        </td>
                        <td className="table-cell text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!student.deleted_at && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditStudent(student);
                                }}
                                className="text-[var(--color-text-accent)] hover:text-[var(--color-accent-oil)] transition-colors"
                                title="Corregir nombre / datos"
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                            )}
                            {student.deleted_at ? (
                              <button
                                onClick={(e) => handleRestore(e, student.id)}
                                className="text-green-500 hover:text-green-400 transition-colors"
                                title="Recuperar estudiante"
                              >
                                <i className="ri-refresh-line"></i>
                              </button>
                            ) : (
                              <button
                                onClick={(e) => handleDelete(e, student)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                                title="Enviar a papelera"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="py-16 text-center">
                <i className="ri-user-search-line text-4xl text-[var(--color-text-muted)]"></i>
                <p className="text-sm text-[var(--color-text-muted)] mt-3">
                  No se encontraron estudiantes con los filtros aplicados
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <StudentModal
          onClose={() => setIsModalOpen(false)}
          onSave={(u) => setStudents((prev) => [...prev, u].sort((a, b) => a.full_name.localeCompare(b.full_name)))}
        />
      )}
      {editStudent && (
        <EditStudentProfileModal
          student={editStudent}
          onClose={() => setEditStudent(null)}
          onSaved={(updated) => {
            setStudents((prev) =>
              prev
                .map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
                .sort((a, b) => a.full_name.localeCompare(b.full_name))
            );
            setEditStudent(null);
          }}
        />
      )}
    </div>
  );
}
